import React, { memo, useMemo, useState } from 'react';

import { ArrowDown, ArrowUp, SystemCheck } from '@pulse-web-ui/icons';
import { Spinner } from '@pulse-web-ui/spinner';
import { useTheme } from '@pulse-web-ui/theme';

import { addTestAttribute } from '@shared/index';

import {
  Label,
  SelectArrow,
  SelectList,
  SelectOption,
  SelectPopover,
  StyledSelect,
} from './select.styles';
import type { Option, SelectProps } from './select.types';

export const Select = memo(
  ({
    options,
    value,
    onChange,
    onBlur,
    ariaLabel,
    disabled,
    label,
    error,
    testId = 'select',
  }: SelectProps) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);

    const selectedOptionLabel = useMemo(
      () => options?.find((option) => option.value === value)?.label,
      [options, value]
    );

    const handleSelectChange = (option: Option) => {
      onChange && onChange(option.value);
    };

    return (
      <StyledSelect
        aria-labelledby={ariaLabel || ''}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onBlur={(e) => {
          setIsOpen(false);
          onBlur && onBlur(e);
        }}
        $isError={error}
        tabIndex={1}
        disabled={disabled}
        {...addTestAttribute(`${testId}.selectInput`)}
      >
        <Label
          $isError={error}
          $disabled={disabled}
          $value={value}
        >
          {label}
        </Label>
        {selectedOptionLabel}
        <SelectArrow $disabled={disabled}>
          {isOpen ? (
            <ArrowUp color={theme.colors.icon.primary} />
          ) : (
            <ArrowDown color={theme.colors.icon.primary} />
          )}
        </SelectArrow>
        {isOpen && (
          <SelectPopover>
            <SelectList>
              {!!options?.length ? (
                options.map((option) => (
                  <SelectOption
                    key={`${option.label}_${option.value}`}
                    onClick={() => {
                      handleSelectChange(option);
                    }}
                    {...addTestAttribute(`${testId}.selectOption`)}
                  >
                    <span>{option.label}</span>
                    {value && option.value === value && (
                      <SystemCheck width={20} />
                    )}
                  </SelectOption>
                ))
              ) : (
                <SelectOption>
                  <Spinner />
                </SelectOption>
              )}
            </SelectList>
          </SelectPopover>
        )}
      </StyledSelect>
    );
  }
);
